import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { AuctionEvent, AuctionEventStatus, WsClientFrame } from '@neon-one/auctions-types';

import { httpClient } from '@/shell/http';
import { useWs } from '@/shell/stores/ws';

// Lifecycle frames that mean this store's cached event.status is stale.
// event.paused/resumed/extended don't change `status` and are handled by
// whichever feature store owns that surface (e.g. run-live).
const LIFECYCLE_FRAME_TYPES = new Set(['event.opened', 'event.closed']);

export const useCurrentAuction = defineStore('currentAuction', () => {
  const id = ref<string | null>(null);
  const event = ref<AuctionEvent | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function fetchAuctionEvent(uuid: string): Promise<AuctionEvent> {
    const resp = await httpClient().get<AuctionEvent>(
      `/admin/auctions/${encodeURIComponent(uuid)}`,
    );
    return resp.data;
  }

  /**
   * `force: true` bypasses the identity cache. Needed when the caller has
   * positive evidence the cached event is stale — e.g. GT1-442's void path
   * getting AUCTION_NOT_LIVE back for an auction we still believe is open.
   */
  async function loadById(uuid: string, opts?: { force?: boolean }): Promise<AuctionEvent> {
    if (!opts?.force && id.value === uuid && event.value) return event.value;
    loading.value = true;
    error.value = null;
    try {
      const data = await fetchAuctionEvent(uuid);
      event.value = data;
      id.value = uuid;
      return data;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unable to load auction';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function setEvent(auction: AuctionEvent): void {
    id.value = auction.uuid;
    event.value = auction;
    error.value = null;
  }

  function clear(): void {
    id.value = null;
    event.value = null;
    error.value = null;
  }

  function status(): AuctionEventStatus | null {
    return event.value?.status ?? null;
  }

  // `event.status` must track live/closing/settling transitions without a
  // page reload (GT1-371): the scheduler and manual admin actions both flip
  // status server-side and broadcast a lifecycle frame on event:{uuid} when
  // they do.
  //
  // ws.ts's subscribe() delivers every frame on every subscribed channel to
  // every registered handler (no per-handler channel filter), so this must
  // check frame.channel itself rather than assume subscribing scopes delivery.
  const ws = useWs();
  let subscribedChannel: string | null = null;

  // Chains lifecycle-triggered refreshes one at a time — each new refresh
  // only starts once the previous one has settled, so a slow response can
  // never land after (and overwrite) a later one.
  //
  // A queued refresh is for whichever auction was current when its frame
  // arrived — never call the identity-clobbering loadById() here, since the
  // store may point at a different auction (or none) by the time this link
  // runs or its request settles. Fetch independently and only commit into
  // event/id when `id` still names the auction this refresh is for, both
  // right before the request starts and again after it resolves.
  let refreshChain: Promise<void> = Promise.resolve();

  function applyFrame(frame: WsClientFrame): void {
    if (!LIFECYCLE_FRAME_TYPES.has(frame.type)) return;
    if (!id.value || frame.channel !== `event:${id.value}`) return;
    const uuid = id.value;
    refreshChain = refreshChain.then(async () => {
      if (id.value !== uuid) return;
      try {
        const fresh = await fetchAuctionEvent(uuid);
        if (id.value === uuid) event.value = fresh;
      } catch (e) {
        if (id.value === uuid) {
          error.value = e instanceof Error ? e.message : 'Unable to load auction';
        }
      }
    });
  }

  watch(id, (next) => {
    if (subscribedChannel) {
      ws.unsubscribe([subscribedChannel], applyFrame);
      subscribedChannel = null;
    }
    if (next) {
      subscribedChannel = `event:${next}`;
      ws.subscribe([subscribedChannel], applyFrame);
    }
  });

  return { id, event, loading, error, loadById, setEvent, clear, status, applyFrame };
});
